import { useEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "@pontimed/ui";
import { Link } from "@tanstack/react-router";

import { useCenterActiveTab } from "@/lib/hooks/use-center-active-tab";

export interface TabItem<T extends string = string> {
  id: T;
  label: ReactNode;
  icon?: ReactNode;
  trailing?: ReactNode;
  /**
   * If set, the tab renders as a TanStack Router `<Link>` instead of a
   * `<button>`. Use for route-driven tab bars (e.g. legal pages). The path
   * MUST already be a known route — type-checked by TanStack Router.
   */
  href?: string;
}

interface TabBarProps<T extends string> {
  tabs: readonly TabItem<T>[];
  activeTab: T;
  /** Called when a button-mode tab is tapped. Not used for `href` tabs. */
  onTabChange?: (id: T) => void;
  /**
   * iOS-style "tap active tab to scroll to top" — wired to
   * SwipeableTabsContent.scrollActiveToTop() via the page's ref.
   */
  onActiveTabClick?: (id: T) => void;
  className?: string;
  /** Default true — sticky top-0 with frosted-glass background. */
  sticky?: boolean;
  /**
   * "primary" (default): page-level outer bar, sticks at top with z-20.
   * "secondary": subtab bar inside an outer panel, sticks with offset matching
   * primary bar height (so they don't overlap when both visible) with z-10.
   */
  variant?: "primary" | "secondary";
}

const SECONDARY_STICKY_TOP = "top-[54px] sm:top-[44px]";

export function TabBar<T extends string>({
  tabs,
  activeTab,
  onTabChange,
  onActiveTabClick,
  className,
  sticky = true,
  variant = "primary",
}: TabBarProps<T>) {
  const tabBarRef = useCenterActiveTab(activeTab);
  const barRef = useRef<HTMLDivElement>(null);
  // Tracks whether the sticky bar is currently pinned at its sticky line.
  // Frosted-glass surface only when stuck — at natural flow position the bar
  // stays transparent so the page surface (HeroBlob, brand gradient) shows
  // through cleanly. Twitter/iOS native pattern.
  const [isStuck, setIsStuck] = useState(false);
  // Overflow affordance: the scrollbar is hidden, so when the strip is wider
  // than the screen (7 tabs at 375px) fade the edge(s) that still hide tabs.
  const [fade, setFade] = useState({ left: false, right: false });

  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const overflow = el.scrollWidth - el.clientWidth;
      const next = {
        left: overflow > 1 && el.scrollLeft > 1,
        right: overflow > 1 && el.scrollLeft < overflow - 1,
      };
      setFade((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    el.addEventListener("scroll", schedule, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener("scroll", schedule);
      ro?.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
    // Re-measure when the tab set changes (labels/badges change scrollWidth).
  }, [tabBarRef, tabs.length]);

  useEffect(() => {
    if (!sticky) return;
    const bar = barRef.current;
    if (!bar) return;

    // Find the nearest scrolling ancestor — apps/app authenticated routes use
    // <main> as the scroll container (overflow-y-auto). For anon/legal pages
    // the document scrolls so we fall back to window.
    let scrollEl: HTMLElement | null = null;
    let p: HTMLElement | null = bar.parentElement;
    while (p && p !== document.body) {
      const cs = getComputedStyle(p);
      if (cs.overflowY === "auto" || cs.overflowY === "scroll") {
        scrollEl = p;
        break;
      }
      p = p.parentElement;
    }

    // Direct scroll-position check, rAF-throttled. Chosen over IntersectionObserver
    // because IO with `rootMargin` + sticky targets behaves inconsistently across
    // browsers (Chromium-derivatives like Vivaldi miss the threshold[1] crossing
    // when the target's first paint already lands at the sticky line) — and we
    // need the stuck transition reliably on every page including initial load.
    let raf = 0;
    const update = () => {
      raf = 0;
      const stickyTopPx = parseFloat(getComputedStyle(bar).top) || 0;
      const barTopInViewport = bar.getBoundingClientRect().top;
      const ancestorTopInViewport = scrollEl?.getBoundingClientRect().top ?? 0;
      // Bar is stuck when its top in viewport equals (or is past) the sticky
      // line, which is at scroll-ancestor-top + stickyTopPx. 0.5px tolerance
      // for sub-pixel rounding on hi-DPI displays.
      setIsStuck(barTopInViewport - ancestorTopInViewport <= stickyTopPx + 0.5);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    // Initial measurement after layout settles — the bar's ref is attached
    // before useEffect, but its getBoundingClientRect can lag by a frame on
    // routes that hydrate async content.
    update();

    const target: EventTarget = scrollEl ?? window;
    target.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      target.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", update);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [sticky]);

  return (
    <div
      ref={barRef}
      className={cn(
        "border-border border-b",
        // iOS frosted-glass — semi-transparent surface + backdrop blur — but
        // ONLY when the bar is actually pinned. At natural flow position the
        // bar is fully transparent so the page surface shows through. Matches
        // Twitter/Instagram/App Store iOS native chrome.
        sticky && "sticky",
        sticky && isStuck && "bg-background/80 lg:bg-card/80 backdrop-blur-md",
        sticky && (variant === "secondary" ? `${SECONDARY_STICKY_TOP} z-10` : "top-0 z-20"),
        className,
      )}
    >
      <div
        ref={tabBarRef}
        className={cn(
          "-mb-px flex overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          fade.left && "mask-l-from-[calc(100%-2rem)]",
          fade.right && "mask-r-from-[calc(100%-2rem)]",
        )}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const classes = cn(
            "relative flex min-h-[44px] flex-1 flex-col items-center justify-center gap-1 border-b-2 px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors sm:flex-row sm:gap-1.5 sm:px-4 sm:text-sm",
            isActive
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground",
          );

          const content = (
            <>
              {tab.icon}
              <span className="flex items-center gap-1.5">
                {tab.label}
                {tab.trailing}
              </span>
            </>
          );

          if (tab.href) {
            return (
              <Link
                key={tab.id}
                to={tab.href as never}
                data-tab-id={tab.id}
                role="tab"
                aria-selected={isActive}
                className={classes}
              >
                {content}
              </Link>
            );
          }

          return (
            <button
              key={tab.id}
              type="button"
              data-tab-id={tab.id}
              onClick={() => {
                if (isActive && onActiveTabClick) {
                  onActiveTabClick(tab.id);
                  return;
                }
                onTabChange?.(tab.id);
              }}
              className={classes}
            >
              {content}
            </button>
          );
        })}
      </div>
    </div>
  );
}
